import React from 'react';
import axios from 'axios';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Host as MfeHost, HostConfigurationResponse, addDatadogFeatureFlagEvaluation, HostProps } from '@economic/react-app';
import { createRoot } from 'react-dom/client';
import { AppDrawerProvider } from '~/app/components/AppDrawer/AppDrawerContext';
import mixpanelTrack from '@economic/mixpanel-service';
import { LegacyFrameIntegration } from './app/shared/components/LegacyFrameIntegration';
import getEnvironment from './utilities/getEnvironment';
import getEnvironmentType from './utilities/getEnvironmentType';
import Application from './components/Application';
import External from './external';
import tracer from './utilities/trace';
import { INTERNAL_API_URL } from './app/shared/util/api/internalApiClient';

export const DATADOG_CONFIG = {
    applicationId: '89086963-d235-4333-b1d9-2551191bdd05',
    clientToken: 'pub2fd1f64a409d19e7277f19765c95d122',
} satisfies HostProps['configuration']['datadog'];

const fetchConfiguration = async () => {
    const host = await axios.get<HostConfigurationResponse>(`${INTERNAL_API_URL}/mfe/host`).then(response => response.data);

    return host;
};

const Host = () => {
    const configuration = {
        datadog: DATADOG_CONFIG,
        fetcher: fetchConfiguration,
        firebaseEmulator: {
            authHost: process.env.FIREBASE_AUTH_EMULATOR_HOST ?? '',
            host: process.env.FIRESTORE_EMULATOR_HOST ?? '',
        },
    } satisfies HostProps['configuration'];

    return (
        <AppDrawerProvider>
            <MfeHost
                configuration={configuration}
                environment={getEnvironment()}
                environmentType={getEnvironmentType()}
                origin={process.env.PUBLIC_URL}
                version={process.env.CLIENT_VERSION ?? 'development'}
                onFlagExposed={(name, value) => {
                    tracer.info(`feature:${name} ${value}`);
                    mixpanelTrack('experiments', 'started', {
                        experimentName: name,
                        experimentVariant: value,
                    });
                    addDatadogFeatureFlagEvaluation(name, value);
                }}>
                <Application />
                <LegacyFrameIntegration path={process.env.ECONOMIC_PATH ?? ''} />
            </MfeHost>
        </AppDrawerProvider>
    );
};

const router = createBrowserRouter([
    { path: '/external/*', Component: External },
    { path: '*', Component: Host },
]);

const Router = () => {
    return <RouterProvider router={router} />;
};

const container = document.getElementById('root')!;
const root = createRoot(container);
root.render(
    <React.StrictMode>
        <Router />
    </React.StrictMode>
);

const params = new URLSearchParams(window.location.search);
const clientVersion = params.get('_x_client_version');

if (clientVersion) {
    import('./utilities/clientVersion').then(({ reportClientVersion }) => {
        reportClientVersion(clientVersion);
    });
}
